/* PDP専用スライダー: 画像のみ構成の商品だけを対象に、クローン経由の無限ループを提供する */
if (typeof SliderComponent !== 'undefined' && !customElements.get('product-gallery-slider')) {
  const { CounterLockController, LoopCloneController, ScrollUtils } =
    window.AuremeProductGallery || {};
  const hasScrollUtilsApi =
    typeof ScrollUtils?.isNearScrollLeft === 'function' &&
    typeof ScrollUtils?.scrollLeftWithoutAnimation === 'function';

  /* ScrollUtils 欠落時も SliderComponent の標準挙動は残し、追加処理だけ縮退させる */
  if (!hasScrollUtilsApi) {
    console.error('[aureme-product-gallery-slider] scroll utils are not loaded.');
  }
  /*
   * Dawn の SliderComponent を基盤にしつつ、AUREME の PDP では
   * 実スライド順を維持したまま loop clone / counter lock / active media 同期を合成する。
   */
  class ProductGallerySlider extends SliderComponent {
    constructor() {
      super();

      if (!this.slider || !this.nextButton) return;

      this.loopCloneController =
        hasScrollUtilsApi && typeof LoopCloneController === 'function'
          ? new LoopCloneController(this)
          : null;
      this.counterLockController =
        hasScrollUtilsApi && typeof CounterLockController === 'function'
          ? new CounterLockController(this)
          : null;

      this.refreshSliderItems();
      this.refreshLoopState();
      this.initPages();
      this.loopCloneController?.connect();
    }

    refreshSliderItems() {
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    }

    getRealSliderItems() {
      return Array.from(this.sliderItems || []);
    }

    getActiveRealSlide() {
      return this.getRealSliderItems().find((item) => item.classList.contains('is-active')) || null;
    }

    getCurrentPageFromActiveSlide() {
      const logicalElement = this.getActiveRealSlide() || this.sliderItemsToShow?.[0] || null;
      if (!logicalElement) {
        return {
          logicalElement: null,
          logicalPage: 1,
        };
      }

      return {
        logicalElement,
        logicalPage: this.sliderItemsToShow.indexOf(logicalElement) + 1,
      };
    }

    isDesktopViewport() {
      return window.matchMedia('(min-width: 990px)').matches;
    }

    isLoopEligibleImageSlide(item) {
      if (!item) return false;

      return (
        Boolean(item.querySelector('.product__modal-opener--image')) &&
        !item.querySelector('deferred-media, product-model')
      );
    }

    shouldEnableInfiniteLoop(realItems = this.getRealSliderItems()) {
      return (
        realItems.length >= 2 && realItems.every((item) => this.isLoopEligibleImageSlide(item))
      );
    }

    isInfiniteLoopEnabled() {
      return this.loopCloneController?.isEnabled() ?? false;
    }

    isLoopJumpInProgress() {
      return this.loopCloneController?.isJumpInProgress() ?? false;
    }

    refreshLoopState() {
      const realItems = this.getRealSliderItems();
      const loopShouldBeEnabled = this.shouldEnableInfiniteLoop(realItems);

      if (!this.loopCloneController) {
        this.removeRenderedLoopClones();
        return;
      }

      if (!loopShouldBeEnabled) {
        this.loopCloneController.disable();
        return;
      }

      this.loopCloneController.sync(realItems);
    }

    removeRenderedLoopClones() {
      for (const clone of this.querySelectorAll('[data-aureme-loop-clone]')) {
        clone.remove();
      }
    }

    resetPages({ syncToActive = true } = {}) {
      this.cancelCounterLock();
      this.refreshSliderItems();
      this.refreshLoopState();
      this.initPages();
      if (!syncToActive) {
        this.cancelSyncToActiveRealSlide();
      }
    }

    scheduleSyncToActiveRealSlide() {
      this.cancelSyncToActiveRealSlide();
      if (!hasScrollUtilsApi) return;

      /*
       * clone 挿入や resetPages 直後は scroll snap の再計算が 1 frame 遅れるため、
       * 2 frame 連続で本物の active slide 位置へ戻し、counter と表示位置を揃える。
       */
      this._syncToActiveRealSlideFrame = requestAnimationFrame(() => {
        this._syncToActiveRealSlideFrame = null;
        this.syncToActiveRealSlide({ behavior: 'auto' });
        this._syncToActiveRealSlideFollowupFrame = requestAnimationFrame(() => {
          this._syncToActiveRealSlideFollowupFrame = null;
          this.syncToActiveRealSlide({ behavior: 'auto' });
        });
      });
    }

    cancelSyncToActiveRealSlide() {
      if (this._syncToActiveRealSlideFrame) {
        cancelAnimationFrame(this._syncToActiveRealSlideFrame);
        this._syncToActiveRealSlideFrame = null;
      }

      if (this._syncToActiveRealSlideFollowupFrame) {
        cancelAnimationFrame(this._syncToActiveRealSlideFollowupFrame);
        this._syncToActiveRealSlideFollowupFrame = null;
      }
    }

    completePageInit() {
      this.update();
      this.scheduleSyncToActiveRealSlide();
    }

    initPages() {
      this.refreshSliderItems();

      const realItems = this.getRealSliderItems();
      const visibleRealItems = realItems.filter((element) => element.clientWidth > 0);

      this.sliderItemsToShow = realItems;

      if (!realItems.length) {
        this.sliderItemOffset = 0;
        this.slidesPerPage = 1;
        this.totalPages = 0;
        this.completePageInit();
        return;
      }

      if (visibleRealItems.length < 2) {
        this.sliderItemOffset = 0;
        this.slidesPerPage = 1;
        this.totalPages = realItems.length;
        this.completePageInit();
        return;
      }

      this.sliderItemOffset = visibleRealItems[1].offsetLeft - visibleRealItems[0].offsetLeft;

      const slidesPerPage = Math.floor(
        (this.slider.clientWidth - visibleRealItems[0].offsetLeft) / this.sliderItemOffset,
      );

      this.slidesPerPage = Math.max(slidesPerPage, 1);
      this.totalPages = realItems.length - this.slidesPerPage + 1;
      this.completePageInit();
    }

    /*
     * SP は clone 領域へ一時的に入り込むため、scrollLeft から「実スライド上の論理ページ」へ戻して
     * counter と slideChanged の契約が clone を外部へ漏らさないようにする。
     */
    getLogicalSlideAtCurrentPosition() {
      if (this.isDesktopViewport()) {
        return this.getCurrentPageFromActiveSlide();
      }

      if (!this.sliderItemsToShow?.length) {
        return {
          logicalElement: null,
          logicalPage: 1,
        };
      }

      if (!this.sliderItemOffset) {
        return this.getCurrentPageFromActiveSlide();
      }

      if (!this.isInfiniteLoopEnabled()) return null;

      const firstRealSlide = this.sliderItemsToShow[0];
      const lastRealSlide = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
      const tolerance = this.loopCloneController.getJumpTolerance();
      const scrollLeft = this.slider.scrollLeft;

      if (scrollLeft < firstRealSlide.offsetLeft - tolerance) {
        return {
          logicalElement: lastRealSlide,
          logicalPage: this.totalPages,
        };
      }

      if (scrollLeft > lastRealSlide.offsetLeft + tolerance) {
        return {
          logicalElement: firstRealSlide,
          logicalPage: 1,
        };
      }

      const indexFromFirst = Math.round(
        (scrollLeft - firstRealSlide.offsetLeft) / this.sliderItemOffset,
      );
      const clampedIndex = Math.min(Math.max(indexFromFirst, 0), this.sliderItemsToShow.length - 1);

      return {
        logicalElement: this.sliderItemsToShow[clampedIndex],
        logicalPage: clampedIndex + 1,
      };
    }

    update() {
      if (!this.slider || !this.nextButton) return;
      if (this.counterLockController?.isLocked()) {
        this.counterLockController.apply();
        this.updateNavigationButtons();
        return;
      }

      const logicalSlide = this.getLogicalSlideAtCurrentPosition();
      if (!logicalSlide) return super.update();

      const previousPage = this.currentPage;
      const { logicalElement, logicalPage } = logicalSlide;
      this.currentPage = logicalPage;

      this.renderCounter(this.currentPage, this.totalPages);

      if (!this.isLoopJumpInProgress() && this.currentPage !== previousPage && logicalElement) {
        this.dispatchEvent(
          new CustomEvent('slideChanged', {
            detail: {
              currentPage: this.currentPage,
              currentElement: logicalElement,
            },
          }),
        );
      }

      this.updateNavigationButtons();
    }

    getCurrentPageFromScroll() {
      if (this.isDesktopViewport()) {
        return this.getCurrentPageFromActiveSlide().logicalPage;
      }

      const logicalSlide = this.getLogicalSlideAtCurrentPosition();
      if (logicalSlide) return logicalSlide.logicalPage;

      return super.getCurrentPageFromScroll();
    }

    /*
     * loop 有効時だけ Dawn 標準の端判定を上書きし、端では clone へ smooth scroll してから
     * LoopCloneController 側の無音復帰に任せる。
     */
    onButtonClick(event) {
      this.cancelCounterLock();

      if (!this.isInfiniteLoopEnabled()) return super.onButtonClick(event);

      event.preventDefault();
      const isNextButton = event.currentTarget.name === 'next';
      const currentPage = this.getCurrentPageFromScroll();

      if (isNextButton && currentPage >= this.totalPages && this.isPageSettled(this.totalPages)) {
        this.loopCloneController.scrollToFirstClone();
        return;
      }

      if (!isNextButton && currentPage <= 1 && this.isPageSettled(1)) {
        this.loopCloneController.scrollToLastClone();
        return;
      }

      if (!this.sliderItemOffset) {
        const targetIndex = isNextButton
          ? Math.min(currentPage, this.sliderItemsToShow.length - 1)
          : Math.max(currentPage - 2, 0);
        const targetSlide = this.sliderItemsToShow[targetIndex];
        if (targetSlide) {
          this.setSlidePosition(targetSlide.offsetLeft);
        }
        return;
      }

      const step = Number(event.currentTarget.dataset.step || 1);
      this.slideScrollPosition = isNextButton
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
      this.setSlidePosition(this.slideScrollPosition);
    }

    goToNextSlideFromTap(currentMediaId) {
      if (!currentMediaId || !this.isInfiniteLoopEnabled()) return false;

      const realSlides = this.getRealSliderItems();
      if (realSlides.length < 2) return false;

      const currentIndex = realSlides.findIndex((item) => item.dataset.mediaId === currentMediaId);
      if (currentIndex < 0) return false;

      if (currentIndex < realSlides.length - 1) return false;

      this.cancelCounterLock();
      return this.loopCloneController.scrollToFirstClone();
    }

    onTouchStart(event) {
      this.cancelCounterLock();
      return super.onTouchStart(event);
    }

    loopToFirstIfNeeded(touchStartPage, touchStartedFromLastBoundary) {
      if (this.isInfiniteLoopEnabled()) return;
      return super.loopToFirstIfNeeded(touchStartPage, touchStartedFromLastBoundary);
    }

    loopToLastIfNeeded(touchStartPage, touchStartedFromFirstBoundary) {
      if (this.isInfiniteLoopEnabled()) return;
      return super.loopToLastIfNeeded(touchStartPage, touchStartedFromFirstBoundary);
    }

    syncToActiveRealSlide({ behavior = 'auto' } = {}) {
      if (!hasScrollUtilsApi) return;

      const activeRealSlide = this.getActiveRealSlide() || this.getRealSliderItems()[0];
      if (!activeRealSlide) return;

      const targetLeft = activeRealSlide.offsetLeft;
      if (ScrollUtils.isNearScrollLeft(this.slider, targetLeft)) return;

      if (behavior === 'auto') {
        ScrollUtils.scrollLeftWithoutAnimation(this.slider, targetLeft);
        return;
      }

      this.slider.scrollTo({ left: targetLeft, behavior });
    }

    renderCounter(currentPage, totalPages) {
      if (!this.currentPageElement || !this.pageTotalElement) return;

      this.currentPageElement.textContent = this.formatCounterValue(currentPage);
      this.pageTotalElement.textContent = this.formatCounterValue(totalPages);
    }

    lockCounterUntilScrollSettled({ currentPage, totalPages = this.totalPages, targetLeft }) {
      this.counterLockController?.lock({ currentPage, totalPages, targetLeft });
    }

    cancelCounterLock() {
      this.counterLockController?.cancel();
    }

    disconnectedCallback() {
      this.counterLockController?.disconnect();
      this.cancelSyncToActiveRealSlide();
      this.loopCloneController?.disconnect();

      if (typeof super.disconnectedCallback === 'function') {
        super.disconnectedCallback();
      }
    }
  }

  customElements.define('product-gallery-slider', ProductGallerySlider);
}
